import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Eye, Lock, Trash2, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useRoomSync } from "@/components/breakout/useRoomSync";
import { GroupBoard } from "@/components/breakout/GroupBoard";
import { RoomChat } from "@/components/breakout/RoomChat";
import { RoomHeader, SummaryBanner } from "@/components/breakout/StudentRooms";

const BASE = "/teacher/breakout/rooms";

const CreateRooms = ({ auth, onDone }) => {
  const [f, setF] = useState({ title: "", topic: "", class_grade: "", count: "2", size: "4" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(BASE, { ...f, count: Number(f.count), size: Number(f.size) }, auth);
      toast.success(`${data.items.length} room(s) opened`); setF({ ...f, title: "", topic: "" }); onDone();
    } catch (er) { toast.error(er.response?.data?.detail?.[0]?.msg || er.response?.data?.detail || "Could not open rooms"); } finally { setBusy(false); }
  };
  const sel = (k, opts, tid) => (
    <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}>
      <SelectTrigger className="mt-1 h-10 bg-white" data-testid={tid}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white">{opts.map((o) => <SelectItem key={o} value={String(o)} data-testid={`${tid}-${o}`}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end" data-testid="breakout-create-form">
      <label className="text-xs font-semibold text-slate-600 lg:col-span-2">Activity title
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required minLength={2} maxLength={60} data-testid="breakout-title" placeholder="e.g. Sensor detectives" className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 text-sm" />
      </label>
      <label className="text-xs font-semibold text-slate-600">Class
        <input value={f.class_grade} onChange={(e) => setF({ ...f, class_grade: e.target.value })} required maxLength={4} data-testid="breakout-class" placeholder="e.g. 9" className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 text-sm" />
      </label>
      <label className="text-xs font-semibold text-slate-600">Rooms{sel("count", [1, 2, 3, 4, 5, 6, 7, 8], "breakout-count")}</label>
      <label className="text-xs font-semibold text-slate-600">Students per room{sel("size", [3, 4, 5], "breakout-size")}</label>
      <Button type="submit" disabled={busy} className="rs-cta rounded-full h-10" data-testid="breakout-create-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open rooms"}</Button>
      <input value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} required minLength={2} maxLength={160} data-testid="breakout-topic"
        placeholder="Discussion topic, e.g. How does an ultrasonic sensor measure distance?" className="sm:col-span-2 lg:col-span-6 h-10 px-3 rounded-md border border-slate-300 text-sm" />
    </form>
  );
};

const Watch = ({ rid, auth, onClosed }) => {
  const { room, events, merge } = useRoomSync(BASE, rid, auth);
  const [closing, setClosing] = useState(false);
  const send = async (text) => { const { data } = await api.post(`${BASE}/${rid}/messages`, { text }, auth); merge([data.event]); return data; };
  const close = async () => {
    setClosing(true);
    try { await api.post(`${BASE}/${rid}/close`, {}, auth); toast.success("Room closed. Zoya wrote the group summary."); onClosed(); }
    catch { toast.error("Could not close the room"); } finally { setClosing(false); }
  };
  if (!room) return <div className="h-64 rounded-3xl bg-slate-100 animate-pulse" />;
  return (
    <div className="space-y-4" data-testid="breakout-watch" data-status={room.status}>
      <RoomHeader room={room} />
      <SummaryBanner room={room} />
      <div className="grid lg:grid-cols-[1fr_340px] gap-4">
        <GroupBoard events={events} readOnly />
        <RoomChat events={events} me="__trainer__" myRole="teacher" closed={room.status !== "open"} onSend={send} teacherView />
      </div>
      {room.status === "open" && <Button onClick={close} disabled={closing} className="rs-cta rounded-full" data-testid="breakout-close-btn">
        {closing ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Zoya is summarising…</> : <><Sparkles className="w-4 h-4 mr-1" />Close room & get Zoya's summary</>}</Button>}
    </div>
  );
};

export const TeacherRooms = ({ auth }) => {
  const [list, setList] = useState(null);
  const [watch, setWatch] = useState(null);
  const load = useCallback(() => api.get(BASE, auth).then((r) => setList(r.data.items)).catch(() => setList([])), [auth]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  const remove = async (id) => {
    try { await api.delete(`${BASE}/${id}`, auth); toast.success("Room deleted"); load(); } catch { toast.error("Could not delete"); }
  };
  return (
    <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-5" data-testid="teacher-breakout-panel">
      <h2 className="font-bold text-lg text-[#0A192F] flex items-center gap-2"><Users className="w-5 h-5 text-[#FF2E63]" />Group breakout rooms</h2>
      <p className="text-xs text-slate-500 mb-4">Students of that class join from the Virtual Classroom → Group tab. Zoya Ma'am moderates every message and writes a summary when you close a room.</p>
      <CreateRooms auth={auth} onDone={load} />
      <ul className="mt-5 divide-y" data-testid="breakout-room-list">
        {list === null ? <li className="py-3 text-sm text-slate-500">Loading…</li> : list.length === 0 ? <li className="py-3 text-sm text-slate-500" data-testid="breakout-empty">No rooms yet.</li>
          : list.map((r) => (
            <li key={r.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`breakout-room-${r.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">{r.title}
                  {r.status === "open" ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold">Open</span>
                    : <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold inline-flex items-center gap-1"><Lock className="w-3 h-3" />Closed</span>}</div>
                <div className="text-xs text-slate-500">Class {r.class_grade} · {r.members.length}/{r.size} · {r.messages} messages{r.blocked ? ` · ${r.blocked} blocked` : ""} · {r.members.join(", ") || "empty"}</div>
              </div>
              <button onClick={() => setWatch(r.id)} data-testid={`breakout-watch-${r.id}`} className="h-9 px-3 rounded-full border border-slate-300 text-xs font-bold inline-flex items-center gap-1"><Eye className="w-4 h-4" />{r.status === "open" ? "Watch" : "Summary"}</button>
              <button onClick={() => remove(r.id)} aria-label="Delete room" data-testid={`breakout-delete-${r.id}`} className="w-9 h-9 rounded-full grid place-items-center border border-slate-300 hover:border-[#FF3B3B] hover:text-[#FF3B3B]"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
      </ul>
      <Dialog open={Boolean(watch)} onOpenChange={(o) => { if (!o) { setWatch(null); load(); } }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto bg-[#F0F4F8]" data-testid="breakout-watch-dialog">
          <DialogHeader><DialogTitle>Breakout room</DialogTitle></DialogHeader>
          {watch && <Watch rid={watch} auth={auth} onClosed={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
